/* eslint-disable camelcase */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation } from '@apollo/client';

// app modules: components
import { CollectionsMatrix } from 'components/Matrix';
import SortingOptions from 'components/SortingOptions';
import SearchField from 'components/SearchField';

// assets
import { COLLECTION_SORT_OPTIONS } from '../../../constants';

const GQL = {
  GET_SETTINGS: gql`
    query GetCollectionViewSortOrderSetting {
      User {
        id
        settings {
          id
          collection_view_sort_order
        }
      }
    }
  `,
  SAVE_SETTINGS: gql`
    mutation UpdateCollectionViewSortOrderSettings($settings: SettingsInput) {
      UpdateUserSettings2(settings: $settings) {
        id
        collection_view_sort_order
      }
    }
  `,
};

function LoggedInWithCollections({ itemProps, favouriteLink }) {
  const [collectionFilter, setCollectionFilter] = useState();
  const userId = useSelector(state => state.user.id);
  const isLoggedIn = useSelector(state => state.user.logged_in);
  const collections = useSelector(state => state.user.collections);
  const [setCollectionViewSortOrder] = useMutation(GQL.SAVE_SETTINGS);
  const availableCollections = Object.values(collections).filter(
    collection => collection.user_id === userId || !collection.user_id,
  );
  const { data, loading } = useQuery(GQL.GET_SETTINGS, {
    skip: !isLoggedIn || userId <= 0,
    ssr: false,
    variables: {
      _authenticated: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return null;
  }

  const currentSortingOption = COLLECTION_SORT_OPTIONS.find(
    option => option.key === (data?.User?.settings?.collection_view_sort_order || 'created_at'),
  );
  const orderBy =
    currentSortingOption.key === 'available_from' ? 'updated_at' : currentSortingOption.key;

  const filteredCollections = collectionFilter?.length
    ? availableCollections.filter(collection =>
        collection.name
          ?.toLowerCase()
          .split(' ')
          .some(word => word.startsWith(collectionFilter?.toLowerCase())),
      )
    : availableCollections;

  const sortedCollections = filteredCollections.sort((a, b) => {
    if (!a[orderBy] || !b[orderBy]) {
      return 1;
    }

    if (currentSortingOption.direction === 'asc') {
      return a[orderBy]?.toLowerCase() > b[orderBy].toLowerCase() ? 1 : -1;
    }
    return a[orderBy].toLowerCase() < b[orderBy].toLowerCase() ? 1 : -1;
  });

  const handleSortingOptionChange = option => {
    setCollectionViewSortOrder({
      variables: { settings: { collection_view_sort_order: option.key } },
      optimisticResponse: {
        UpdateUserSettings2: {
          __typename: 'Settings',
          id: userId,
          collection_view_sort_order: option.key,
        },
      },
    });
  };

  if (loading) {
    return null;
  }

  return (
    <div>
      <div className="my-collections__controls-row">
        <SortingOptions
          selected={{
            orderBy: currentSortingOption.key,
            orderByDirection: currentSortingOption.direction,
          }}
          options={COLLECTION_SORT_OPTIONS}
          sortingOptionClickHandler={handleSortingOptionChange}
        />
        <div className="my-collections__find-wrapper">
          <SearchField
            autoComplete="off"
            fullWidth
            size="small"
            name="q"
            label="Find collection"
            placeholder="Find collection"
            value={collectionFilter}
            onChange={e => setCollectionFilter(e.target.value)}
            inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off' }}
          />
        </div>
      </div>
      <CollectionsMatrix
        withAddOption
        prefixItems={[favouriteLink]}
        items={sortedCollections}
        showAll
        itemProps={itemProps}
      />
    </div>
  );
}
export default LoggedInWithCollections;

LoggedInWithCollections.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  itemProps: PropTypes.object.isRequired,
  favouriteLink: PropTypes.node.isRequired,
};
